import { SMBeamModel, BeamMaterial } from './types'

// Client for the saved-designs API (api/BeamDesigns). Designs are owned per user and
// filtered per material; the whole SMBeamModel (incl. reinforcement + stirrups) round-trips.

export interface DesignSummary {
  id: string
  name: string
  materialType: BeamMaterial
  updatedAtUtc: string
}

export interface DesignDetail extends DesignSummary {
  model: SMBeamModel
}

type SessionHeaders = Record<string, string>

async function describeError(response: Response): Promise<string> {
  let detail = `HTTP ${response.status}`
  try {
    const body = await response.json()
    if (body && body.error) {
      detail += ` – ${body.error}`
    }
  } catch {
    // non-JSON body (empty 404/500)
  }
  return detail
}

export async function listDesigns(material: BeamMaterial, headers: SessionHeaders): Promise<DesignSummary[]> {
  const response = await fetch(`/api/BeamDesigns?materialType=${encodeURIComponent(material)}`, { headers })
  if (!response.ok) {
    throw new Error(`Failed to list designs (${await describeError(response)})`)
  }
  return await response.json() as DesignSummary[]
}

export async function saveDesign(material: BeamMaterial, name: string, model: SMBeamModel, headers: SessionHeaders): Promise<DesignSummary> {
  const response = await fetch('/api/BeamDesigns', {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, materialType: material, model })
  })
  if (!response.ok) {
    throw new Error(`Failed to save design (${await describeError(response)})`)
  }
  return await response.json() as DesignSummary
}

export async function loadDesign(id: string, headers: SessionHeaders): Promise<DesignDetail> {
  const response = await fetch(`/api/BeamDesigns/${id}`, { headers })
  if (!response.ok) {
    throw new Error(`Failed to load design (${await describeError(response)})`)
  }
  return await response.json() as DesignDetail
}

export async function deleteDesign(id: string, headers: SessionHeaders): Promise<void> {
  const response = await fetch(`/api/BeamDesigns/${id}`, { method: 'DELETE', headers })
  if (!response.ok) {
    throw new Error(`Failed to delete design (${await describeError(response)})`)
  }
}
